import { useState } from "react";

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { noop } from "neetocist";
import { Table } from "neetoui";
import PropTypes from "prop-types";
import { T } from "ramda";
import { createPortal } from "react-dom";

import Row from "./Row";

const sensorConfig = { activationConstraint: { distance: 8 } };

const DndTable = ({
  rowData,
  columnData,
  overlayColumnIndex = 0,
  isRowDroppable = T,
  onDrop = noop,
  ...rest
}) => {
  const [dragState, setDragState] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, sensorConfig));

  const onDragEnd = ({ active, over }) => {
    setDragState(null);
    if (!active || !over) return;
    onDrop(active.data.current, over.data.current);
  };

  const renderOverlay = data => {
    const colData = columnData[overlayColumnIndex];
    const rowData = data[colData.dataIndex];

    return (
      <div className="flex px-20">
        <div className="neeto-ui-rounded-lg neeto-ui-bg-white px-4">
          {colData.render ? colData.render(rowData, data) : rowData}
        </div>
      </div>
    );
  };

  return (
    <DndContext {...{ onDragEnd, sensors }} onDragStart={setDragState}>
      <Table
        {...{ ...rest, columnData, rowData }}
        components={{ body: { row: Row } }}
        onRow={(data, index) => ({
          data,
          index,
          isDroppable: isRowDroppable(data),
        })}
      />
      {createPortal(
        <DragOverlay modifiers={[restrictToVerticalAxis]}>
          {dragState?.active && renderOverlay(dragState.active.data?.current)}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

DndTable.propTypes = {
  /** The data for the rows of the table. */
  rowData: PropTypes.arrayOf(PropTypes.object).isRequired,

  /** The data for the columns of the table. */
  columnData: PropTypes.arrayOf(PropTypes.object).isRequired,

  /** The index of the column to display the overlay for. */
  overlayColumnIndex: PropTypes.number,

  /** A function that determines if a row is droppable based on checks on the data. */
  isRowDroppable: PropTypes.func,

  /** A function that is called when a row is dropped. */
  onDrop: PropTypes.func.isRequired,
};

export default DndTable;
